import type { SelectOption } from '@/components/SearchableSelect'
import type { WarehouseLedgerTransactionItem } from '@/services/warehouseTypes'

export function formatLedgerCaseCount(signedCases: number): string {
  const value = Number(signedCases)
  if (!Number.isFinite(value)) return '0'
  const abs = Math.abs(value)
  const magnitude =
    Number.isInteger(abs) || Math.abs(abs - Math.round(abs)) < 0.005
      ? String(Math.round(abs))
      : abs.toFixed(2)
  return value < 0 ? `(${magnitude})` : magnitude
}

export function formatLedgerMatchPickerLabel(params: {
  txn_date: string
  bk_sku: string
  product_name?: string | null
  signed_cases: number
}): string {
  const date = params.txn_date?.trim() || '—'
  const product = (params.product_name || '').trim()
  const skuPart = params.bk_sku?.trim() || '—'
  const namePart = product ? ` ${product}` : ''
  const cases = formatLedgerCaseCount(params.signed_cases)
  return `${date} · ${skuPart}${namePart} · ${cases} cases`
}

export function compareLedgerTransactionsByDateAsc(
  a: WarehouseLedgerTransactionItem,
  b: WarehouseLedgerTransactionItem
): number {
  const dateCmp = (a.txn_date || '').localeCompare(b.txn_date || '')
  if (dateCmp !== 0) return dateCmp
  const skuCmp = (a.bk_sku || '').localeCompare(b.bk_sku || '')
  if (skuCmp !== 0) return skuCmp
  return a.bk_txn_id.localeCompare(b.bk_txn_id)
}

export function sortLedgerTransactionsByDateAsc(
  txns: WarehouseLedgerTransactionItem[]
): WarehouseLedgerTransactionItem[] {
  return [...txns].sort(compareLedgerTransactionsByDateAsc)
}

const NON_LINKABLE_PRODUCT_KINDS = new Set([
  'store_draw',
  'initial_store_transfer',
])

export function buildProductLedgerMatchOptions(
  txns: WarehouseLedgerTransactionItem[],
  currentLineId?: number | null
): SelectOption[] {
  return sortLedgerTransactionsByDateAsc(txns)
    .filter((txn) => !NON_LINKABLE_PRODUCT_KINDS.has(txn.kind))
    // Already linked to this line show as chips — omit from the add-another picker
    // so re-selecting them can't look like a successful new link.
    .filter(
      (txn) =>
        currentLineId == null || txn.product_invoice_line_id !== currentLineId
    )
    .map((txn) => ({
      id: txn.bk_txn_id,
      label: formatLedgerMatchPickerLabel({
        txn_date: txn.txn_date,
        bk_sku: txn.bk_sku,
        product_name: txn.product_name,
        signed_cases: txn.signed_cases,
      }),
      disabled: txn.product_invoice_line_id != null,
    }))
}

export function buildBillingLedgerMatchOptions(
  txns: WarehouseLedgerTransactionItem[]
): SelectOption[] {
  return sortLedgerTransactionsByDateAsc(txns).map((txn) => ({
    id: txn.bk_txn_id,
    label: formatLedgerMatchPickerLabel({
      txn_date: txn.txn_date,
      bk_sku: txn.bk_sku,
      product_name: txn.product_name,
      signed_cases: txn.signed_cases,
    }),
    disabled: txn.billing_line_id != null,
  }))
}
